// (c) chaehongee. Confidential and proprietary.

import { useCallback, useState } from 'react'
import { uploadNode, importNodeFromUrl, type NodeUploadType } from '../../../../services/node'
import { addNode } from '../../../../store/slices/sessionSlice'
import { setNodes as setSelectedNodes } from '../../../../store/slices/selectionSlice'
import {
    AI_MODEL_DRAG_TYPES,
    isAiModelDrag,
    isAiModelDragging,
    setAiModelDragging,
} from '../../../Backstage/utils/drag'
import type { UploadAPI, SessionLike } from './types'

/**
 * Canvas Upload Feature Hook
 * 
 * Handles drag & drop file uploads and URL imports.
 * Supports both file drags and URL drags (e.g., from AI models page).
 */
export function useCanvasDrop(
    session: SessionLike | null,
    dispatch: (action: any) => void,
    readOnly: boolean = false
): UploadAPI {
    const [isDragOver, setIsDragOver] = useState(false)
    const [isUploading, setIsUploading] = useState(false)
    const isExternalMediaDrag = (e: React.DragEvent<HTMLDivElement>): boolean => {
        const items = Array.from(e.dataTransfer?.items || [])
        if (items.length > 0) {
            const hasFile = items.some(
                (it) =>
                    it.kind === 'file' &&
                    typeof it.type === 'string' &&
                    (it.type.startsWith('image/') || it.type.startsWith('video/'))
            )
            if (hasFile) return true
        }
        // Accept URL drags (e.g., from AIModelsPage setting text/uri-list)
        const types = new Set(Array.from(e.dataTransfer?.types || []))
        if (types.has('text/uri-list') || types.has('text/plain')) {
            return true
        }
        if (AI_MODEL_DRAG_TYPES.some((type) => types.has(type))) return true
        if (isAiModelDragging()) return true
        return types.has('Files')
    }

    const parseDragUrls = (e: React.DragEvent<HTMLDivElement>): string[] => {
        const uriList = e.dataTransfer.getData('text/uri-list')
        const text = e.dataTransfer.getData('text/plain')
        const merged = [uriList, text].filter(Boolean).join('\n')
        if (!merged) return []
        const urlPattern = /^(https?:|blob:|data:)/i
        const urls = merged
            .split(/\r?\n/)
            .map((s) => s.trim())
            .filter((s) => s && urlPattern.test(s))
        // Deduplicate
        return Array.from(new Set(urls))
    }

    const inferMediaTypeFromUrl = (url: string): NodeUploadType => {
        const lower = url.toLowerCase()
        if (/\.(mp4|webm|mov|m4v|ogg)(\?|#|$)/.test(lower)) return 'video'
        return 'image'
    }

    const fetchUrlAsFile = async (url: string): Promise<File | null> => {
        try {
            const isSpecialScheme = /^(blob:|data:)/i.test(url)
            const fetchOptions: RequestInit = {}
            if (!isSpecialScheme) {
                fetchOptions.mode = 'cors'
                fetchOptions.credentials = 'omit'
                try {
                    const resolved = new URL(url, window.location.href)
                    if (resolved.origin === window.location.origin) {
                        fetchOptions.credentials = 'include'
                    }
                } catch {
                    // Ignore URL parsing issues; fall back to omit credentials.
                }
            }
            const res = await fetch(url, fetchOptions)
            if (!res.ok) return null
            const blob = await res.blob()
            const nameFromPath = (() => {
                try {
                    const u = new URL(url)
                    const base = u.pathname.split('/').pop() || 'media'
                    return base.split('?')[0] || 'image'
                } catch {
                    return 'media'
                }
            })()
            const ext = blob.type?.split('/')[1] || 'bin'
            const filename = nameFromPath.includes('.') ? nameFromPath : `${nameFromPath}.${ext}`
            return new File([blob], filename, { type: blob.type || 'application/octet-stream' })
        } catch {
            return null
        }
    }

    const emitAiModelDrop = useCallback((nodeId: string) => {
        if (!session) return
        dispatch(setSelectedNodes({ sessionId: session.id, nodeIds: [nodeId] }))
        window.dispatchEvent(
            new CustomEvent('backstage:ai-model-drop', { detail: { nodeId } })
        )
    }, [dispatch, session])

    const onDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>) => {
        // Prevent drop in read-only mode
        if (readOnly) return
        // Only handle media files or media URLs
        if (!isExternalMediaDrag(e)) return
        e.preventDefault()
        e.stopPropagation()
        setIsDragOver(false)
        setAiModelDragging(false)
        const isAiModelDrop =
            isAiModelDrag(e.dataTransfer) || isAiModelDragging()
        if (!session) return
        const files = Array.from(e.dataTransfer.files || [])
        let mediaFiles = files.filter(
            (f) =>
                f &&
                typeof f.type === 'string' &&
                (f.type.startsWith('image/') || f.type.startsWith('video/'))
        )
        // If no files, attempt to treat as URL drags
        if (mediaFiles.length === 0) {
            const urls = parseDragUrls(e)
            if (urls.length > 0) {
                const fetched = await Promise.all(
                    urls.map(async (url) => ({
                        url,
                        file: await fetchUrlAsFile(url),
                    }))
                )
                const fetchedFiles = fetched
                    .map((item) => item.file)
                    .filter((f): f is File => Boolean(f))
                mediaFiles = fetchedFiles
                // If still no files (likely CORS blocked), fallback to server-side import-by-url for each URL
                if (mediaFiles.length === 0) {
                    try {
                        const settled = await Promise.all(
                            urls.map((u) =>
                                importNodeFromUrl(
                                    u,
                                    session.id,
                                    inferMediaTypeFromUrl(u)
                                )
                                    .then((node) => ({ ok: true as const, node }))
                                    .catch((error) => ({ ok: false as const, error, url: u }))
                            )
                        )
                        let didImport = false
                        let lastImportedNodeId: string | null = null
                        for (const r of settled) {
                            if (r.ok) {
                                dispatch(addNode({ sessionId: session.id, node: r.node }))
                                didImport = true
                                lastImportedNodeId = r.node.id
                            } else {
                                // eslint-disable-next-line no-console
                                console.error('Import-by-url failed for', (r as any)?.url, (r as any)?.error)
                            }
                        }
                        if (didImport && isAiModelDrop && lastImportedNodeId) {
                            emitAiModelDrop(lastImportedNodeId)
                        }
                    } finally {
                        // No further file-based uploads in this branch
                    }
                    return
                }
            }
        }
        if (mediaFiles.length === 0) return
        try {
            setIsUploading(true)
            // Run uploads in parallel and collect results with their file context
            const settled = await Promise.all(
                mediaFiles.map((file) =>
                    uploadNode(
                        file,
                        session.id,
                        file.type.startsWith('video/') ? 'video' : 'image'
                    )
                        .then((node) => ({ ok: true as const, node, file }))
                        .catch((error) => ({ ok: false as const, error, file }))
                )
            )
            let didUpload = false
            let lastUploadedNodeId: string | null = null
            for (const result of settled) {
                if (result.ok) {
                    const { node } = result
                    dispatch(addNode({ sessionId: session.id, node }))
                    didUpload = true
                    lastUploadedNodeId = node.id
                } else {
                    // eslint-disable-next-line no-console
                    console.error('Upload failed for file', (result.file as any)?.name, result.error)
                }
            }
            if (didUpload && isAiModelDrop && lastUploadedNodeId) {
                emitAiModelDrop(lastUploadedNodeId)
            }
        } finally {
            setIsUploading(false)
        }
    }, [dispatch, emitAiModelDrop, session, readOnly])

    const onDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        if (readOnly || !isExternalMediaDrag(e)) return
        e.preventDefault()
        e.stopPropagation()
        setIsDragOver(true)
    }, [readOnly])

    const onDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        if (!isExternalMediaDrag(e)) return
        e.preventDefault()
        e.stopPropagation()
        setIsDragOver(false)
    }, [])

    return {
        isDragOver,
        isUploading,
        handlers: { onDrop, onDragOver, onDragLeave },
    }
}





